import { useEffect } from 'react';
import { Select } from '@wajeez-web/components/mui';
import { ButtonHeadless, InputLabel } from '@wajeez-web/components/ui';
import {
  FlowsActions,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';
import {
  SubmitHandler,
  useFieldArray,
  useForm,
  Controller,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import { useParams } from 'react-router-dom';
import { Server } from '@wajeez/wajeez.microservices';

interface FlowNavigationsFormProps {
  flowId?: number | null;
  version?: string;
}

type Inputs = {
  navigations?: {
    value: string | undefined;
    label: string | undefined;
    options: { value: string | undefined; label: string | undefined }[];
  }[];
};

export const FlowNavigationsForm = ({
  flowId,
  version,
}: FlowNavigationsFormProps) => {
  const { id } = useParams<{ id: string }>();

  const contentDispatch = useContentDispatch();

  const flowDetails = useContentSelector(({ flows }) => flows.flowDetailsById);
  const flowDetailsData = flowDetails.data;

  const flowDetailsV2 = useContentSelector(
    ({ flows }) => flows.flowDetailsByIdV2
  );
  const flowDetailsV2Data = flowDetailsV2.data;

  const setFlowNavigationsStatus = useContentSelector(
    ({ flows }) => flows.setFlowContentEntityTypeNavigations.status
  );

  const contentEntityTypeLookups = useContentSelector(
    ({ contentEntityType }) => contentEntityType.lookups
  );

  const navigations = useContentSelector(
    ({ contentEntityType }) => contentEntityType.navigations
  );
  const navigationsData = navigations.data;

  const { t } = useTranslation();

  const { handleSubmit, control } = useForm<Inputs>();

  const { fields, replace, update } = useFieldArray({
    control,
    name: 'navigations',
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (id) {
      const res = await contentDispatch(
        FlowsActions.setFlowContentEntityTypeNavigationsAsync({
          flowId: parseInt(id),
          contentEntityTypeNavigationIds: data.navigations
            ?.filter((n) => n.value)
            .map((n) => parseInt(n.value!)),
        })
      );

      if (res.meta.requestStatus === 'fulfilled') {
        if (version !== 'v2') {
          contentDispatch(FlowsActions.getFlowDetailsByIdAsync(parseInt(id)));
        } else {
          contentDispatch(FlowsActions.getFlowDetailsByIdV2Async(parseInt(id)));
        }
      }
    }
  };

  useEffect(() => {
    replace(
      contentEntityTypeLookups.map((l) => {
        let current: Server.TypeNavigationDashboardOutput | undefined;

        if (version !== 'v2') {
          current = flowDetailsData?.typeNavigations?.find(
            (tn) => tn.contentEntityTypeId === l.id
          );
        } else {
          current = flowDetailsV2Data?.typeNavigations?.find(
            (tn) => tn.contentEntityTypeId === l.id
          );
        }

        return {
          value: current?.contentEntityTypeNavigationId?.toString(),
          label: l.displayNamePlural + ` (${l.name})`,
          options: navigationsData
            .filter((n) => n.contentEntityTypeId === l.id)
            .map((n) => {
              return {
                value: n.id?.toString(),
                label: n.navigationName,
              };
            }),
        };
      })
    );
  }, [
    contentEntityTypeLookups.length,
    navigationsData.length,
    flowDetailsData,
    flowDetailsV2Data,
    version,
  ]);

  const renderNavigationInputs = () => {
    return fields.map((f, index) => {
      return (
        <div key={f.id} className="relative w-full">
          <InputLabel
            text={
              t(`inputs.labels.${f.label}`, f.label?.toString() || '') as string
            }
            name={f.id as string}
            required={false}
          />
          <div className="">
            <Controller
              name={`navigations.${index}.label` as const}
              control={control}
              render={({ field: { onChange, ...otherField } }) => {
                return (
                  <Select
                    options={f.options || []}
                    option={{
                      value: f?.value,
                      label: f?.label,
                    }}
                    onChange={(option) => {
                      update(index, { ...fields[index], value: option.value });
                    }}
                    withEmptyOption={true}
                    autoWidth={false}
                    {...otherField}
                  />
                );
              }}
            />
          </div>
        </div>
      );
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full max-w-[300px]"
      noValidate
    >
      <div className="w-full flex flex-col space-y-3">
        {renderNavigationInputs()}
      </div>
      <div className="w-[70px] absolute top-6 left-6">
        <ButtonHeadless paddingYStyle="py-2" type="submit">
          <div className="w-full flex justify-center items-center text-white">
            <ReturnTranslatedText
              origintext="Save"
              text={`buttons.${'Save'}`}
            />
            {setFlowNavigationsStatus === 'loading' && (
              <BiLoaderAlt className="mr-1 animate-spin w-5" />
            )}
          </div>
        </ButtonHeadless>
      </div>
    </form>
  );
};
